import React from "react";

const getHeaderColor = (percent, isDarkTheme) => {
    const pct = parseFloat(String(percent || "0").replace('%', '')) || 0;
    const overTargetGray = isDarkTheme ? "rgb(75, 75, 75)" : "rgb(110, 110, 110)";
    if (pct >= 100) return overTargetGray;
    if (pct < 100 && pct >= 66.66) return "rgb(186, 77, 81)";
    if (pct < 66.66 && pct >= 33.33) return "rgb(232, 194, 103)";
    if (pct < 33.33) return "rgb(154, 181, 126)";
    return overTargetGray;
};

const getStackHeaderBorderColor = (headerColor) => {
    const match = headerColor.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
    if (!match) return "rgba(255,255,255,0.2)";
    const factor = 0.68;
    return `rgb(${Math.round(parseInt(match[1]) * factor)}, ${Math.round(parseInt(match[2]) * factor)}, ${Math.round(parseInt(match[3]) * factor)})`;
};

const formatOwnerName = (name) => {
    if (!name) return "";
    const words = name.trim().split(/\s+/);
    if (words.length === 1) return words[0];
    const formatted = [words[0]];
    for (let i = 1; i < words.length; i++) {
        formatted.push(words[i][0].toUpperCase());
    }
    return formatted.join(" ");
};

const EMPTY_CARD_STATE = {};

// Memoized card: with large ticket counts (hundreds of cards) a toggle used to
// re-render every card from scratch right before the open/close transition
// started, stealing the animation's first frames.  toggleCard now preserves
// referential identity for unchanged cardState objects, so React.memo lets
// untouched cards skip re-rendering entirely.
const TicketCard = React.memo(function TicketCard({
    card,
    index,
    cardState,
    isLastCard,
    isDarkTheme,
    themeStyles,
    compactStatusStyle,
    entryAnimStage,
    isReady,
    disableBodyTransition,
    disableHeaderTransition,
    isOpenTicketPlacementTop,
    headerLabelMode,
    imageError,
    onToggle,
    onImageError,
    hasUserInteractedRef,
    isEntryAnimatingRef,
    headerSpanRefs,
    cardRefs,
}) {
    const isExpanded = Boolean(cardState.isExpanded);
    const renderMode = (cardState.renderMode === "open" || isExpanded)
        ? "open"
        : (cardState.renderMode === "medium" ? "medium" : "collapsed");
    // Stage 1: all cards show as collapsed (slim header strips during slide-in).
    // Stages 2-3 and idle: show actual renderMode.
    const isCollapsedMode = renderMode === "collapsed";
    const isMediumMode = renderMode === "medium";
    const isBodyOpen = renderMode === "open";
    const cardStaggerDelay = Math.min(index, 8) * 55;
    // Body fades in via CSS keyframe (delay baked in) — no React state
    // change needed mid-animation, so no reflow stutter.
    const bodyAnimation = "none";
    const fullHeight = Number(cardState.fullHeight || 0);
    const visibleBodyHeight = Number(cardState.visibleBodyHeight || 0);
    // index.css declares `* { box-sizing: content-box !important }`, which
    // overrides any inline boxSizing style.  With content-box, flex-basis is the
    // CONTENT height — padding is added on top.  To keep the card's visual body
    // height at exactly visibleBodyHeight (e.g. 209 px), we set flex-basis to
    // (visibleBodyHeight − paddingTop − paddingBottom).  The budget math in
    // calculateAndAdjustHeight already uses visibleBodyHeight as the visual height,
    // so no budget changes are needed — only this flex-basis adjustment.
    const bodyPaddingPxPerSide = compactStatusStyle.bodyPaddingPx; // 8 px
    const bodyFlexBasisPx = renderMode === "open"
        ? Math.max(0, (visibleBodyHeight || fullHeight) - 2 * bodyPaddingPxPerSide)
        : 0;
    const bodyHeight = `${bodyFlexBasisPx.toFixed(3)}px`;
    const bodyPadding = renderMode === "open" ? `${compactStatusStyle.bodyPaddingPx}px` : "0";
    const contentOpacity = isBodyOpen ? 1 : 0;
    const contentTransitionDisabled = ((entryAnimStage === 1 || entryAnimStage === 2) && !hasUserInteractedRef.current) || disableBodyTransition;
    const contentTransition = (delayMs) => contentTransitionDisabled ? "none" : `opacity 680ms cubic-bezier(0.22, 1, 0.36, 1) ${delayMs}ms`;
    const normalHeaderPadPx = 2;
    const normalHeaderMinHeightPx = 22;
    const normalHeaderFontRem = compactStatusStyle.normalHeaderFontRem;

    const mediumHeaderPadPx = normalHeaderPadPx;
    const mediumHeaderMinHeightPx = normalHeaderMinHeightPx;
    const mediumHeaderFontRem = normalHeaderFontRem;

    const collapsedHeaderPadPx = 0;
    const collapsedHeaderMinHeightPx = 3;
    const collapsedHeaderFontRem = Math.max(0.44, Number((mediumHeaderFontRem - 0.18).toFixed(2)));

    const headerPadPx = isCollapsedMode
        ? collapsedHeaderPadPx
        : isMediumMode
            ? mediumHeaderPadPx
            : normalHeaderPadPx;
    const headerMinHeightPx = isCollapsedMode
        ? collapsedHeaderMinHeightPx
        : isMediumMode
            ? mediumHeaderMinHeightPx
            : normalHeaderMinHeightPx;
    const headerFontSize = isCollapsedMode
        ? `${collapsedHeaderFontRem}rem`
        : isMediumMode
            ? `${mediumHeaderFontRem}rem`
            : `${normalHeaderFontRem}rem`;
    const headerLeftPad = isCollapsedMode ? "1px" : `${compactStatusStyle.headerLeftPadPx}px`;
    const headerLineHeight = isCollapsedMode ? 1 : isMediumMode ? 1.12 : 1.2;
    const headerDisplayText = isCollapsedMode ? "" : `${card.headerText || ""}`;
    const headerColor = getHeaderColor(card.percent, isDarkTheme);
    const cardGapPx = isDarkTheme ? "5px" : "3px";
    const marginBottomPx = isCollapsedMode
        ? (cardState.lastCollapsedMode
            ? (isOpenTicketPlacementTop
                ? (cardState.toggledEnabled ? cardGapPx : "0px")
                : cardGapPx)
            : "0px")
        : (isLastCard ? "0px" : cardGapPx);

    return (
        <div
            className="card"
            ref={(el) => (cardRefs.current[index] = el)}
            style={{
                backgroundColor: themeStyles.cardBackground,
                border: isCollapsedMode ? "none" : themeStyles.cardBorder,
                boxShadow: isCollapsedMode ? "none" : themeStyles.cardBoxShadow,
                color: themeStyles.bodyTextColor,
                borderRadius: (isMediumMode || isCollapsedMode) ? "0px" : "5px",
                opacity: isReady ? undefined : 0,
                animation: (entryAnimStage === 1 && isReady && !hasUserInteractedRef.current)
                    ? `widgetCardAppear 1ms step-end ${cardStaggerDelay}ms both`
                    : "none",
                marginBottom: marginBottomPx,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                // Promote each card to its own compositor layer.  During open/close
                // toggles every card below the toggled one shifts each frame; without
                // layers the browser REPAINTS the entire shifted region every frame
                // (measured: 5 frames >33ms per toggle at 300 cards / 800×950px).
                // As layers, the compositor moves them without repainting (measured:
                // zero frames >33ms, solid frame rate).  Collapsed stripes are tiny
                // (~5px tall) so per-layer memory is negligible.
                willChange: "transform",
            }}
            onMouseEnter={(event) => {
                if (!isReady || isEntryAnimatingRef.current) return;
                event.currentTarget.style.animation = "none";
                event.currentTarget.style.transition = "transform 420ms cubic-bezier(0.34, 1.56, 0.64, 1)";
                event.currentTarget.style.transform = "translateX(3px)";
            }}
            onMouseLeave={(event) => {
                if (!isReady || isEntryAnimatingRef.current) return;
                event.currentTarget.style.transition = "transform 300ms cubic-bezier(0.25, 0.46, 0.45, 0.94)";
                event.currentTarget.style.transform = "translateX(0px)";
            }}
        >
            <div
                className="card-header fw-bold"
                style={{
                    cursor: "pointer",
                    overflow: "hidden",
                    paddingTop: `${headerPadPx}px`,
                    paddingBottom: `${headerPadPx}px`,
                    minHeight: `${headerMinHeightPx}px`,
                    fontSize: headerFontSize,
                    lineHeight: headerLineHeight,
                    backgroundColor: headerColor,
                    border: isCollapsedMode ? !isDarkTheme ? `1px solid #fff` : `1px solid ${getStackHeaderBorderColor(headerColor)}` : `1px solid ${headerColor}`,
                    color: compactStatusStyle.showHeaderText ? "#ffffff" : "transparent",
                    WebkitFontSmoothing: "antialiased",
                    MozOsxFontSmoothing: "grayscale",
                    textRendering: "optimizeLegibility",
                    letterSpacing: "0.02em",
                    borderTopLeftRadius: isCollapsedMode || isMediumMode ? "0" : "4px",
                    borderTopRightRadius: isCollapsedMode || isMediumMode ? "0" : "4px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    paddingLeft: headerLeftPad,
                    paddingRight: headerLeftPad,
                    transform: isCollapsedMode ? "none" : "translateZ(0)",
                    backfaceVisibility: "hidden",
                    WebkitBackfaceVisibility: "hidden",
                    transition: disableHeaderTransition
                        ? "color 250ms ease"
                        : (() => {
                            const hMs = (entryAnimStage === 2 && !hasUserInteractedRef.current) ? 220 : 420;
                            return `color 250ms ease, min-height ${hMs}ms cubic-bezier(0.25, 0.46, 0.45, 0.94), font-size ${hMs}ms cubic-bezier(0.25, 0.46, 0.45, 0.94), padding ${hMs}ms cubic-bezier(0.25, 0.46, 0.45, 0.94), line-height ${hMs}ms cubic-bezier(0.25, 0.46, 0.45, 0.94), border-radius ${hMs}ms cubic-bezier(0.25, 0.46, 0.45, 0.94)`;
                        })(),
                }}
                title={headerDisplayText}
                onClick={() => onToggle(index)}
            >
                <span
                    ref={el => { headerSpanRefs.current[card.id] = el; }}
                    style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: "1 1 0", minWidth: 0 }}
                >
                    {headerDisplayText}
                </span>
                {!isCollapsedMode && card.percent && (
                    <span style={{ flexShrink: 0, marginLeft: "6px", whiteSpace: "nowrap" }}>
                        {headerLabelMode === 'full'
                            ? `${card.percent} of Target`
                            : card.percent}
                    </span>
                )}
            </div>

            <div
                className="card-body collapse show"
                style={{
                    backgroundColor: themeStyles.cardBackground,
                    color: themeStyles.bodyTextColor,
                    overflow: "hidden",
                    animation: bodyAnimation,
                    // flex-basis drives card height — MUST NOT overshoot.
                    // Old easing cubic-bezier(0.34, 1.08, 0.64, 1) had Y1=1.08>1
                    // which caused flex-basis to temporarily reach ~108% of the
                    // target, making open cards ~17px taller during the animation
                    // peak and clipping the last card at the bottom of the widget.
                    // cubic-bezier(0.25, 0.46, 0.45, 0.94) is a smooth ease-out
                    // (all Y values ≤ 1) that stays within the target height.
                    // padding is also transitioned so it animates in sync with flex-basis —
                    // without this, padding jumps to 8 px at t=0 while flex-basis is still
                    // near 0, causing a 16 px visual pop at the start of the open animation.
                    transition: disableBodyTransition ? "none" : "flex-basis 900ms cubic-bezier(0.25, 0.46, 0.45, 0.94), padding 900ms cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 700ms cubic-bezier(0.22, 1, 0.36, 1)",
                    flexBasis: bodyHeight,
                    flexGrow: 0,
                    flexShrink: 0,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-start",
                    gap: isBodyOpen ? `${compactStatusStyle.bodyRowGapPx}px` : "0",
                    padding: bodyPadding,
                    margin: "0",
                    opacity: isBodyOpen ? 1 : 0,
                    pointerEvents: (isBodyOpen && entryAnimStage === 0) ? "auto" : "none",
                    borderRadius: "0",
                    // boxSizing: "border-box" is intentionally omitted.
                    // index.css forces `* { box-sizing: content-box !important }`
                    // on all elements, overriding any inline boxSizing value.
                    // flex-basis is already adjusted above to account for padding.
                    contain: "layout paint",
                    // Compositing hints only for medium/open cards — promoting every
                    // collapsed stripe (hundreds at large ticket counts) creates a
                    // layer explosion that stutters the toggle animation.
                    transform: isCollapsedMode ? "none" : "translateZ(0)",
                    backfaceVisibility: "hidden",
                    willChange: (!disableBodyTransition && !isCollapsedMode) ? "flex-basis, padding, opacity" : "auto",
                }}
            >
                {/* Collapsed stripes skip body content entirely — with very
                    large ticket counts (100+ collapsed cards) rendering every
                    body's avatar/title/dates costs hundreds of DOM nodes per
                    widget and stutters parallel-widget entry animations.
                    Content mounts when the card becomes medium/open (it is
                    invisible until then anyway: opacity 0, flex-basis 0). */}
                {!isCollapsedMode && (<>
                <div
                    className="row align-items-start"
                    style={{
                        flex: "0 0 auto",
                        opacity: contentOpacity,
                        transition: contentTransition(200),
                    }}
                >
                    <div className="col text-start">
                        <div style={{ fontSize: `${compactStatusStyle.topLeftFontRem}rem`, fontWeight: "bold" }}>
                            <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                Target: {card.targetHours != null && !isNaN(Number(card.targetHours)) ? +Number(card.targetHours).toFixed(2) : "NA"} hrs
                            </div>
                            <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                Actual: {card.actualHours != null && !isNaN(Number(card.actualHours)) ? +Number(card.actualHours).toFixed(2) : "NA"} hrs
                            </div>
                        </div>
                    </div>
                    <div className="col d-flex flex-column align-items-end">
                        <div style={{ width: `${compactStatusStyle.avatarWrapWidthPx}px`, display: "flex", justifyContent: "center" }}>
                            {!card.imageUrl || imageError ? (
                                <svg
                                    width={compactStatusStyle.avatarSizePx}
                                    height={compactStatusStyle.avatarSizePx}
                                    viewBox="0 0 200 200"
                                    xmlns="http://www.w3.org/2000/svg"
                                    style={{ borderRadius: "50%" }}
                                >
                                    <circle cx="100" cy="100" r="78" fill="#EBCDB2" stroke="#E3B187" strokeWidth="1.5" />
                                    <circle cx="100" cy="68" r="26" fill="#B8B8B8" />
                                    <path d="M72 100 L128 100 L100 178 Z" fill="#B8B8B8" />
                                </svg>
                            ) : (
                                <img
                                    src={card.imageUrl}
                                    alt="avatar"
                                    style={{
                                        width: compactStatusStyle.avatarSizePx,
                                        height: compactStatusStyle.avatarSizePx,
                                        borderRadius: "50%",
                                    }}
                                    onError={() => onImageError(card.id)}
                                />
                            )}
                        </div>

                        <div
                            style={{
                                width: `${compactStatusStyle.avatarWrapWidthPx}px`,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                textAlign: "center",
                                color: themeStyles.bodyTextColor,
                                fontSize: `${compactStatusStyle.ownerFontRem}rem`,
                            }}
                            title={card.owner}
                        >
                            {formatOwnerName(card.owner)}
                        </div>
                    </div>
                </div>

                <div
                    style={{
                        flex: "1 1 auto",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        paddingTop: `${compactStatusStyle.centerRowPadPx}px`,
                        paddingBottom: `${compactStatusStyle.centerRowPadPx}px`,
                        minHeight: `${compactStatusStyle.centerRowMinHeightPx}px`,
                        overflow: "hidden",
                        minWidth: 0,
                    }}
                >
                    <p
                        className="mt-0 fw-bold text-center"
                        style={{
                            display: "-webkit-box",
                            WebkitLineClamp: compactStatusStyle.titleLines,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            fontSize: `${compactStatusStyle.titleFontRem}rem`,
                            lineHeight: 1.2,
                            // Explicit maxHeight ensures webkit-line-clamp clips with "…"
                            // before the parent overflow:hidden cuts mid-glyph.
                            maxHeight: `${(compactStatusStyle.titleFontRem * 1.2 * compactStatusStyle.titleLines + 0.1).toFixed(2)}rem`,
                            flexShrink: 0,
                            marginTop: "0",
                            marginBottom: `${compactStatusStyle.titleMarginBottomRem}rem`,
                            opacity: contentOpacity,
                            transition: contentTransition(360),
                        }}
                        title={card.title}
                    >
                        {card.title}
                    </p>

                    <p
                        className="mt-1 text-center"
                        style={{
                            color: themeStyles.mutedTextColor,
                            display: "-webkit-box",
                            WebkitLineClamp: compactStatusStyle.descLines,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            fontSize: `${compactStatusStyle.descFontRem}rem`,
                            lineHeight: 1.2,
                            maxHeight: `${(compactStatusStyle.descFontRem * 1.2 * compactStatusStyle.descLines + 0.1).toFixed(2)}rem`,
                            flexShrink: 0,
                            marginTop: "0",
                            marginBottom: `${compactStatusStyle.descMarginBottomRem}rem`,
                            opacity: contentOpacity,
                            transition: contentTransition(520),
                        }}
                        title={card.description}
                    >
                        {card.description}
                    </p>
                    {card.description1 && (
                        <p
                            className="text-center"
                            style={{
                                color: themeStyles.mutedTextColor,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                fontSize: `${compactStatusStyle.descFontRem}rem`,
                                lineHeight: 1.2,
                                marginTop: "0",
                                marginBottom: `${compactStatusStyle.titleMarginBottomRem}rem`,
                                opacity: contentOpacity,
                                transition: contentTransition(440),
                            }}
                            title={card.description1}
                        >
                            {card.description1}
                        </p>
                    )}
                </div>

                <div
                    className="row small"
                    style={{
                        flex: "0 0 auto",
                        alignItems: "center",
                        fontSize: `${compactStatusStyle.metaFontRem}rem`,
                        lineHeight: 1.2,
                        paddingTop: `${compactStatusStyle.metaRowPadPx}px`,
                        marginLeft: 0,
                        marginRight: 0,
                        opacity: contentOpacity,
                        transition: contentTransition(680),
                    }}
                >
                    <div className="col" style={{ paddingLeft: `${compactStatusStyle.metaRowPadPx}px`, paddingRight: `${compactStatusStyle.metaRowPadPx}px` }}>
                        <b>Opened</b>
                        <br />
                        {card.openDate ?? "NA"}
                    </div>
                    <div className="col text-center" style={{ paddingLeft: `${compactStatusStyle.metaRowPadPx}px`, paddingRight: `${compactStatusStyle.metaRowPadPx}px` }}>
                        <div style={{ display: "inline-block", textAlign: "left" }}>
                            <b>Promised</b>
                            <br />
                            {card.promiseDate ?? "NA"}
                        </div>
                    </div>
                    <div className="col text-end" style={{ paddingLeft: `${compactStatusStyle.metaRowPadPx}px`, paddingRight: `${compactStatusStyle.metaRowPadPx}px` }}>
                        <div style={{ display: "inline-block", textAlign: "left" }}>
                            <b>Completed</b>
                            <br />
                            {card.completedDate ?? card.status ?? "NA"}
                        </div>
                    </div>
                </div>
                </>)}
            </div>
        </div>
    );
});

export { EMPTY_CARD_STATE };
export default TicketCard;
